import React, { useEffect, useRef } from "react";

import useDebouncedCallback from "@mapmycustomers/shared/util/hook/useDebouncedCallback";

import { useMap } from "@app/component/map/FeaturedMap";
import simplifyPath from "@app/scene/map/utils/simplify";
import GeoPath from "@app/types/GeoPath";
import { LASSO_POLYLINE_OPTIONS, MIN_LASSO_POINTS } from "@app/util/lasso";

interface Props {
  onAdd: (path: GeoPath) => void;
  onCancel: () => void;
}

const Drafter: React.FC<Props> = ({ onAdd, onCancel }) => {
  const { map } = useMap();

  const prevX = useRef(0);
  const prevY = useRef(0);

  const panBy = useDebouncedCallback([
    (x, y) => {
      const diffX = prevX.current - x;
      const diffY = prevY.current - y;

      prevX.current = x;
      prevY.current = y;

      map.panBy(diffX, diffY);
    },
    50,
  ]);

  useEffect(() => {
    let lassoPolyline: google.maps.Polyline;
    let listeners: google.maps.MapsEventListener[] = [];

    map.setOptions({
      draggableCursor: "crosshair",
    });

    const cancelPolyline = () => {
      if (lassoPolyline) {
        lassoPolyline.setVisible(false);
        lassoPolyline.setMap(null);
      }

      map.setOptions({
        draggable: true,
      });
    };

    const handleKeyDown = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") {
        clearListeners();
        cancelPolyline();
        onCancel();
      } else if (ev.key === "Shift") {
        map.setOptions({
          draggableCursor: "move",
        });
      }
    };

    const handleKeyUp = (ev: KeyboardEvent) => {
      if (ev.key === "Shift") {
        map.setOptions({
          draggableCursor: "crosshair",
        });
      }
    };

    const clearListeners = () => {
      listeners.forEach((listener) => listener.remove());
      listeners = [];

      document.body.removeEventListener("keydown", handleKeyDown);
      document.body.removeEventListener("keyup", handleKeyUp);
    };

    const drawStartListener = google.maps.event.addListener(map, "mousedown", () => {
      clearListeners();
      cancelPolyline();

      lassoPolyline = new google.maps.Polyline(LASSO_POLYLINE_OPTIONS);
      lassoPolyline.setMap(map);

      map.setOptions({
        draggable: false,
        draggableCursor: "crosshair",
      });

      listeners.push(
        google.maps.event.addListener(map, "mousemove", (event: google.maps.MapMouseEvent) => {
          if (!event.latLng) {
            return;
          }

          const mouseEvent = event.domEvent as MouseEvent;
          const position = new google.maps.LatLng(event.latLng.lat(), event.latLng.lng());

          if (mouseEvent.shiftKey) {
            panBy(mouseEvent.clientX, mouseEvent.clientY);
          } else {
            lassoPolyline.getPath().push(position);
          }
        })
      );

      listeners.push(
        google.maps.event.addListenerOnce(map, "mouseup", (event: google.maps.MapMouseEvent) => {
          if (!event.latLng) {
            return;
          }
          const position = new google.maps.LatLng(event.latLng.lat(), event.latLng.lng());
          lassoPolyline.getPath().push(position);

          const completedPath = simplifyPath(lassoPolyline.getPath().getArray(), 1.0);
          const isValid = completedPath.length >= MIN_LASSO_POINTS;

          // Duplicate starting point at the end, to make sure lasso is closed
          const firstPoint = new google.maps.LatLng(completedPath[0]);
          completedPath.push(firstPoint);

          clearListeners();
          cancelPolyline();

          if (isValid) {
            onAdd(completedPath);
          }
        })
      );

      document.body.addEventListener("keydown", handleKeyDown);
      document.body.addEventListener("keyup", handleKeyUp);
    });

    return () => {
      drawStartListener?.remove();
      clearListeners();

      lassoPolyline?.setMap(null);

      map.setOptions({
        draggable: true,
        draggableCursor: "",
      });
    };
  }, [map, onAdd, onCancel, panBy]);

  return null;
};

export default Drafter;
